import React, { useEffect, useState } from "react";

import { isDictationSupported } from "./useDictation.js";
import { Icon, IconButton } from "../components/ui";
import { useEntitlement } from "../contexts";

/**
 * Hidden entirely — not disabled — when there is no microphone or the page is
 * not a secure context. A control that can never work is noise.
 *
 * The device probe is async and permission-gated: before the user has granted
 * access, `enumerateDevices` reports inputs with empty labels but still reports
 * them, which is all we need. Browsers that refuse the enumeration are treated
 * as "probably has one" — the permission prompt is then the honest answer.
 */
const DictateButton = ({ onClick, recording = false, label = "Dictate a listing", ...rest }) => {
  const [hasMic, setHasMic] = useState(null);
  // "Voice dictation (listings & requests)" — the Feature Matrix row. Hidden,
  // not locked, like the no-microphone case: it is an extra on a page the user
  // already has. The API refuses /listings/dictate and /requests/dictate too.
  const allowed = useEntitlement("voice_dictation");

  useEffect(() => {
    let live = true;
    if (!isDictationSupported()) {
      setHasMic(false);
      return () => { live = false; };
    }
    if (!navigator.mediaDevices?.enumerateDevices) {
      setHasMic(true);
      return () => { live = false; };
    }
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (live) setHasMic(devices.some((d) => d.kind === "audioinput"));
      })
      .catch(() => {
        if (live) setHasMic(true);
      });
    return () => { live = false; };
  }, []);

  if (!hasMic || !allowed) return null;

  return (
    <IconButton
      size="xl"
      round
      pressed={recording}
      onClick={onClick}
      label={label}
      title={label}
      data-btn="listing-dictate"
      {...rest}
    >
      <Icon name="Mic" size="md" />
    </IconButton>
  );
};

export default DictateButton;
